import { useEffect, useState } from 'react'
import { Button } from './ui.tsx'
import { eur } from '../lib/format.ts'
import type { TaskDefinition, User } from '../api/types.ts'

export interface Activation {
  userIds?: string[]
  valueCents?: number
  markNotDone?: boolean
  reason?: string
}

/**
 * Poner una tarea hoy.
 *
 * Dos usos en el mismo cuadro porque son el mismo gesto con distinto final:
 * «hay lavadora, que la tiendan» y «esto se paga». El castigo no es una
 * excepción del modelo: crea la misma instancia de siempre, ya descontada, así
 * que aparece en su lista con su importe, se puede alegar y se puede deshacer.
 */
export function ActivateDialog({
  open,
  task,
  candidates,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean
  task: TaskDefinition | null
  candidates: User[]
  busy?: boolean
  onCancel: () => void
  onConfirm: (activation: Activation) => void
}) {
  const [userIds, setUserIds] = useState<string[]>([])
  const [amount, setAmount] = useState('')
  const [penalize, setPenalize] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) {
      setUserIds([])
      setAmount('')
      setPenalize(false)
      setReason('')
    }
  }, [open, task?.id])

  if (!open || !task) return null

  const isBonus = task.kind === 'BONUS'
  // En una rotativa le toca a quien le toca, y en una abierta la reclama quien
  // quiera: elegir destinatario solo tiene sentido en las fijas.
  const eligible =
    task.assignmentMode === 'FIXED'
      ? candidates.filter((c) => task.assignees.some((a) => a.userId === c.id))
      : []

  const cents = amount.trim() === '' ? undefined : Math.round(Number(amount.replace(',', '.')) * 100)
  const amountInvalid = cents !== undefined && (!Number.isFinite(cents) || cents < 0)
  const missingReason = penalize && reason.trim().length === 0

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
      <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] p-5">
        <h2 className="text-lg font-semibold break-words">{task.title}</h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          {penalize
            ? 'Se le descuenta ahora mismo, sin esperar a nada.'
            : 'Aparecerá hoy en su móvil, con aviso.'}
        </p>

        {eligible.length > 1 && (
          <div className="mt-4 flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">¿Para quién?</span>
            <div className="flex flex-wrap gap-2">
              {eligible.map((c) => {
                const on = userIds.length === 0 || userIds.includes(c.id)
                return (
                  <button
                    key={c.id}
                    onClick={() =>
                      setUserIds((prev) =>
                        prev.includes(c.id)
                          ? prev.filter((id) => id !== c.id)
                          : // Vacío significa «todos»; al elegir uno hay que
                            // partir de una lista explícita.
                            (prev.length === 0 ? [c.id] : [...prev, c.id]),
                      )
                    }
                    className={`min-h-11 rounded-xl border px-3 text-sm ${
                      on
                        ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                        : 'border-[var(--color-line)]'
                    }`}
                  >
                    {c.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <label className="mt-4 flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">
            {isBonus ? 'Importe (€)' : penalize ? 'Cuánto le quitas (€)' : 'Importe (€)'}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={
              isBonus
                ? `Por defecto ${eur(task.bonusAmountCents ?? 0)}`
                : 'Por defecto, lo que valga una tarea esta semana'
            }
            className="w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 py-2 text-[16px]"
          />
        </label>

        {!isBonus && (
          <>
            <label className="mt-4 flex items-center justify-between gap-3">
              <span className="text-[15px]">
                Descontarlo ya
                <span className="block text-xs text-[var(--color-muted)]">
                  Para un castigo: no hay nada que puedan hacer para evitarlo
                </span>
              </span>
              <input
                type="checkbox"
                className="h-6 w-6 shrink-0"
                checked={penalize}
                onChange={(e) => setPenalize(e.target.checked)}
              />
            </label>

            {penalize && (
              <label className="mt-3 flex flex-col gap-1">
                <span className="text-sm text-[var(--color-muted)]">Por qué</span>
                <textarea
                  rows={3}
                  value={reason}
                  placeholder="Lo verá en su móvil y en el desglose…"
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full resize-none rounded-xl border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 py-2 text-[16px]"
                />
              </label>
            )}
          </>
        )}

        <div className="mt-4 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant={penalize ? 'danger' : 'primary'}
            className="flex-1"
            disabled={busy || amountInvalid || missingReason}
            onClick={() =>
              onConfirm({
                ...(userIds.length > 0 ? { userIds } : {}),
                ...(cents !== undefined ? { valueCents: cents } : {}),
                ...(penalize ? { markNotDone: true, reason: reason.trim() } : {}),
              })
            }
          >
            {busy ? 'Aplicando…' : penalize ? 'Descontar' : 'Activar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
